import { redirect } from 'react-router';
import { msalInstance } from '../providers/msalProvider';
import { isInTeams } from '../common/Teams';
import { hasAuthenticationParams, isAuthenticationRedirect } from './MsalUtils';
import { Constants } from '../common/Constants';

/**
 * Wrapper function that ensures a user is authenticated before executing a loader.
 * If not authenticated, redirects to the home page where the login flow can be initiated.
 */
export function withAuthentication<T extends any[], R>(
  loader: (...args: T) => Promise<R> | R
) {
  return async (...args: T): Promise<R> => {
    // If we're in Teams or WebComponent, authentication is handled differently
    if (isInTeams() || Constants.IS_WEBCOMPONENT) {
      return loader(...args);
    }

    // If we're handling an authentication redirect, don't block the loader
    if (isAuthenticationRedirect() || hasAuthenticationParams()) {
      return loader(...args);
    }

    // Check if the user is authenticated
    const accounts = (msalInstance as any).getAllAccounts();
    
    if (!accounts || accounts.length === 0) {
      console.warn('User not authenticated, redirecting to home for login');
      throw redirect('/');
    }

    // User is authenticated, proceed with the original loader
    return loader(...args);
  };
}

/**
 * Helper function to check if authentication is available without throwing
 */
export function isAuthenticated(): boolean {
  // If we're in Teams or WebComponent, assume authentication is handled
  if (isInTeams() || Constants.IS_WEBCOMPONENT) {
    return true;
  }

  // Check if user has active account
  const accounts = (msalInstance as any).getAllAccounts();
  return accounts && accounts.length > 0;
}